/**
 * CapabilityResultsView — post-submit results screen: shows the ResultsView
 * (personal score / pillars / recommendations), prompts the user to upload
 * certifications referenced in their answers, lists their uploaded certs with
 * expiry badges, and hosts the upload + edit-dates dialogs.
 *
 * Pure presentational; the parent (CapabilityMetricsPage) owns all state and
 * side-effects.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ChevronLeft, Award, Loader2, FileText, Clock, AlertTriangle, CalendarDays,
  Upload, ShieldCheck, Trash2,
} from 'lucide-react';
import { ResultsView } from './ResultsView';
import { ShareEmailDialog } from './ShareEmailDialog';

const certStatusColors = { pending: '#FF9500', verified: '#34C759', rejected: '#FF3B30' };
const expiryColors = { expired: '#FF3B30', critical: '#FF3B30', expiring_soon: '#FF9500', expiring: '#FFCC00', valid: '#34C759', none: '#9CA3AF' };

const expiryLabel = (c) => {
  if (!c.expiry_date) return null;
  const d = c.days_until_expiry;
  if (d === null || d === undefined) return null;
  if (d < 0) return `Expired ${-d}d ago`;
  if (d === 0) return 'Expires today';
  return `Expires in ${d}d`;
};

export const CapabilityResultsView = ({
  // Data
  result,
  pendingCertsToUpload,
  myCerts,
  // Cert-upload dialog
  certDialogOpen,
  certUploadName,
  certFile,
  certIssueDate,
  certExpiryDate,
  certUploading,
  // Edit-dates dialog
  datesDialogOpen,
  editingCert,
  editIssueDate,
  editExpiryDate,
  // Share dialog
  shareDialogOpen,
  sharing,
  // Setters
  setView,
  setCertDialogOpen,
  setCertUploadName,
  setCertFile,
  setCertIssueDate,
  setCertExpiryDate,
  setDatesDialogOpen,
  setEditIssueDate,
  setEditExpiryDate,
  setShareDialogOpen,
  // Handlers
  onShareResults,
  onRefreshRecs,
  onOpenEditDates,
  onDeleteCert,
  onCertUpload,
  onSaveDates,
  onShareSend,
}) => {
  return (
    <div data-testid="assessment-results">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => setView('list')} data-testid="back-to-list-results">
          <ChevronLeft className="w-4 h-4 mr-1" />Back to Assessments
        </Button>
      </div>
      <Card className="border-[#E5E7EB]">
        <CardHeader className="border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-[#002FA7]" />
            <CardTitle>Your Assessment Results</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <ResultsView submission={result} onShareResults={onShareResults} onRefreshRecs={onRefreshRecs} />
        </CardContent>
      </Card>

      {/* Certification Upload Prompt */}
      {pendingCertsToUpload.length > 0 && (
        <Card className="border-[#FF9500]/30 bg-[#FF9500]/[0.02] mt-4" data-testid="cert-upload-prompt">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#FF9500]" />Verify Your KM Certifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-[#4B5563] mb-3">
              You selected the following KM certifications. Upload your certificates for HR verification.
            </p>
            <div className="space-y-2">
              {pendingCertsToUpload.map(cert => {
                const uploaded = myCerts.find(c => c.certification_name === cert);
                return (
                  <div key={cert} className="flex items-center justify-between p-2.5 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`cert-prompt-${cert}`}>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-[#002FA7]" />
                      <span className="text-sm font-medium text-[#0A0A0A]">{cert}</span>
                    </div>
                    {uploaded ? (
                      <Badge className="text-[8px]" style={{ backgroundColor: certStatusColors[uploaded.status] + '15', color: certStatusColors[uploaded.status] }}>
                        {uploaded.status === 'pending' ? 'Under Review' : uploaded.status === 'verified' ? 'Verified' : 'Rejected'}
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-[#002FA7] hover:bg-[#00227A]"
                        onClick={() => { setCertUploadName(cert); setCertDialogOpen(true); }}
                        data-testid={`upload-cert-${cert}`}
                      >
                        <Upload className="w-3 h-3 mr-1" />Upload Certificate
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Certifications */}
      {myCerts.length > 0 && (
        <Card className="border-[#E5E7EB] mt-4" data-testid="my-certifications">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#002FA7]" />My Uploaded Certifications
              </CardTitle>
              {(() => {
                const expiring = myCerts.filter(c => c.expiry_date && c.days_until_expiry !== null && c.days_until_expiry !== undefined && c.days_until_expiry <= 60);
                if (expiring.length === 0) return null;
                const expired = expiring.filter(c => c.days_until_expiry < 0).length;
                return (
                  <Badge className="text-[9px] flex items-center gap-1" style={{ backgroundColor: '#FF3B3015', color: '#FF3B30' }} data-testid="expiring-summary-badge">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {expired > 0 ? `${expired} expired` : `${expiring.length} expiring soon`}
                  </Badge>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myCerts.map(c => {
                const ec = expiryColors[c.expiry_status] || '#9CA3AF';
                const el = expiryLabel(c);
                return (
                  <div key={c.cert_id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-[#E5E7EB]" data-testid={`my-cert-${c.cert_id}`}>
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <Badge className="text-[8px]" style={{ backgroundColor: certStatusColors[c.status] + '15', color: certStatusColors[c.status] }}>{c.status}</Badge>
                      <span className="text-sm font-medium">{c.certification_name}</span>
                      <span className="text-[10px] text-[#9CA3AF] truncate">{c.file_name}</span>
                      {el && (
                        <Badge className="text-[8px] flex items-center gap-0.5" style={{ backgroundColor: ec + '15', color: ec }} data-testid={`expiry-badge-${c.cert_id}`}>
                          <Clock className="w-2.5 h-2.5" />{el}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {c.reviewer_notes && <span className="text-[9px] text-[#9CA3AF] mr-2">{c.reviewer_notes}</span>}
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-[#002FA7]" onClick={() => onOpenEditDates(c)} data-testid={`edit-dates-${c.cert_id}`}>
                        <CalendarDays className="w-3 h-3 mr-0.5" />Dates
                      </Button>
                      {c.status === 'pending' && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[#FF3B30]" onClick={() => onDeleteCert(c.cert_id)} data-testid={`delete-cert-${c.cert_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={certDialogOpen} onOpenChange={setCertDialogOpen}>
        <DialogContent data-testid="cert-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#002FA7]" />Upload Certificate: {certUploadName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              Upload your <strong>{certUploadName}</strong> certificate document (PDF, JPG, PNG — max 10MB). It will be reviewed by HR for verification.
            </p>
            <div>
              <Label className="text-xs">Certificate File *</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setCertFile(e.target.files?.[0] || null)} className="mt-1" data-testid="cert-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Issue Date</Label>
                <Input type="date" value={certIssueDate} onChange={e => setCertIssueDate(e.target.value)} className="mt-1" data-testid="cert-issue-date-input" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  Expiry Date <span className="text-[9px] text-[#9CA3AF]">(enables renewal alerts)</span>
                </Label>
                <Input type="date" value={certExpiryDate} onChange={e => setCertExpiryDate(e.target.value)} className="mt-1" data-testid="cert-expiry-date-input" />
              </div>
            </div>
            <p className="text-[10px] text-[#9CA3AF]">
              Tip: Adding an expiry date triggers automatic reminders to you and HR at 60, 30, and 7 days before expiry.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={onCertUpload} disabled={certUploading || !certFile} data-testid="submit-cert-upload-btn">
              {certUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dates Dialog */}
      <Dialog open={datesDialogOpen} onOpenChange={setDatesDialogOpen}>
        <DialogContent data-testid="cert-edit-dates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#002FA7]" />
              Edit Certificate Dates{editingCert ? `: ${editingCert.certification_name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              Update the issue and expiry dates. Changing these resets renewal alert tracking so you'll get fresh reminders at 60/30/7 days.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Issue Date</Label>
                <Input type="date" value={editIssueDate} onChange={e => setEditIssueDate(e.target.value)} className="mt-1" data-testid="edit-issue-date-input" />
              </div>
              <div>
                <Label className="text-xs">Expiry Date</Label>
                <Input type="date" value={editExpiryDate} onChange={e => setEditExpiryDate(e.target.value)} className="mt-1" data-testid="edit-expiry-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDatesDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={onSaveDates} data-testid="save-cert-dates-btn">Save Dates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareEmailDialog
        open={shareDialogOpen}
        onClose={setShareDialogOpen}
        title="Share Results via Email"
        onSend={onShareSend}
        sending={sharing}
      />
    </div>
  );
};
